import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaSort,
  FaSortDown,
  FaSortUp,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";

import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { Corban } from "@/models/corban.model";
import {
  SplitStatusColors,
  SplitStatusEnum,
  SplitStatusLabels,
} from "@/models/splitStatusEnum.model";
import {
  checkPendingCorbans,
  updateCorban,
  updateSplitSolicitationStatus,
} from "@/services/corban.service";
import {
  deleteFinancialSettings,
  updateFinancialSettings,
} from "@/services/financialSettings.service";
import { documentFormatter } from "@/utils/documentFormatter";
import { useDebounce } from "@/utils/useDebounce";

import AnalizeSplitApproveData from "../AnalizeSplitApproveData/AnalizeSplitApproveData";
import CreditAuctionProposalsPagination from "../CreditAuctionProposalsPage/credit-auction-proposals-pagination";
import {
  EDGE_COLUMN_PX,
  tableCellStyles,
  tableHeaderStyles
} from "../FinancialShared/tableStyles";
import RejectReasonModal from "../RejectReasonModal/RejectReasonModal";

interface SplitApprovalTabProps {
  searchTerm: string;
  statusFilter?: string;
}

export default function SplitApprovalTab({
  searchTerm,
  statusFilter = "",
}: SplitApprovalTabProps) {
  const backgroundStyle = useConditionalBackground();
  const toast = useToast();

  const debouncedSearch = useDebounce(searchTerm, 500);

  const [corbans, setCorbans] = useState<Corban[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [selectedCorban, setSelectedCorban] = useState<Corban | undefined>(
    undefined
  );
  const [isApproving, setIsApproving] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectModalLoading, setRejectModalLoading] = useState(false);

  const {
    isOpen: isAnalysisOpen,
    onOpen: onOpenAnalysis,
    onClose: onCloseAnalysis,
  } = useDisclosure();

  // ── helpers de status ──
  const getSplitStatus = (corban: Corban): SplitStatusEnum | null => {
    const splitSolicitation = corban?.splitSolicitation;
    if (splitSolicitation?.status) {
      return splitSolicitation.status as SplitStatusEnum;
    }

    if (
      corban.FinancialSettings?.status === "PENDING" ||
      corban.status === "PENDING"
    ) {
      return SplitStatusEnum.CREATED;
    }

    if (
      corban.FinancialSettings?.status === "APPROVED" &&
      corban.status === "APPROVED"
    ) {
      return SplitStatusEnum.SPLIT_APPROVED;
    }

    if (
      corban.FinancialSettings?.status === "REJECTED" ||
      corban.status === "REJECTED"
    ) {
      return SplitStatusEnum.FINANCIAL_REJECTED;
    }

    return null;
  };

  const getSplitStatusLabel = (corban: Corban): string => {
    const status = getSplitStatus(corban);
    if (status && SplitStatusLabels[status]) {
      return SplitStatusLabels[status];
    }
    return "Sem status";
  };

  const getSplitStatusColor = (corban: Corban): string => {
    const status = getSplitStatus(corban);

    if (!status) return "gray";

    switch (status) {
      case SplitStatusEnum.CREATED:
      case SplitStatusEnum.FINANCIAL_ANALYSIS:
        return "orange";
      case SplitStatusEnum.FINANCIAL_APPROVED:
      case SplitStatusEnum.CONTRACT_SENT:
      case SplitStatusEnum.CONTRACT_SIGNED:
        return "blue";
      case SplitStatusEnum.SPLIT_APPROVED:
        return "green";
      case SplitStatusEnum.FINANCIAL_REJECTED:
      case SplitStatusEnum.CANCELLED_EXPIRED:
        return "red";
      default:
        return "gray";
    }
  };

  const getApproveButtonText = (status: SplitStatusEnum | null): string => {
    if (!status) return "Aprovar";

    switch (status) {
      case SplitStatusEnum.CREATED:
        return "Aprovar - Financeiro";
      case SplitStatusEnum.FINANCIAL_APPROVED:
        return "Enviar Contrato";
      default:
        return "Aprovar";
    }
  };

  const getRejectButtonText = (status: SplitStatusEnum | null): string => {
    if (!status) return "Recusar";

    switch (status) {
      case SplitStatusEnum.CREATED:
        return "Recusar - Financeiro";
      case SplitStatusEnum.FINANCIAL_APPROVED:
      case SplitStatusEnum.CONTRACT_SIGNED:
        return "Recusar - Cadastro";
      case SplitStatusEnum.SPLIT_APPROVED:
        return "Recusar - Split";
      default:
        return "Recusar";
    }
  };
  // ── fetch ──

  const fetchPendingCorbans = useCallback(async () => {
    try {
      const response = await checkPendingCorbans(
        page,
        pageSize,
        sortBy,
        sortDirection,
        debouncedSearch.trim(),
        statusFilter
      );
      setCorbans(response?.data ?? []);

      setTotalPages(response?.totalPages ?? 1);
    } catch (error) {
      console.error("Erro ao obter corbans pendentes:", error);
    }
  }, [page, pageSize, sortBy, sortDirection, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchPendingCorbans();
  }, [fetchPendingCorbans]);

  // Volta para a primeira página ao mudar filtro de status ou busca
  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  // ── sorting ──

  const handleSort = (field: string) => {
    setSortBy(field);
    setSortDirection((prev) =>
      sortBy === field && prev === "asc" ? "desc" : "asc"
    );
  };

  // ── approve / reject ──

  const handleApprove = async () => {
    if (!selectedCorban?.id) return;

    setIsApproving(true);
    try {
      const currentStatus = getSplitStatus(selectedCorban);
      // Cada etapa pode aplicar mais de uma transição: a timeline do backend
      // empilha um evento pendente para o próximo status, então pular um
      // status deixaria o final fora do histórico.
      let nextStatuses: SplitStatusEnum[] = [];
      let message = "";

      switch (currentStatus) {
        case SplitStatusEnum.CREATED:
        case SplitStatusEnum.FINANCIAL_ANALYSIS:
          nextStatuses = [SplitStatusEnum.FINANCIAL_APPROVED];
          message = "Aprovado pelo Financeiro. Encaminhado ao Cadastro.";
          break;
        case SplitStatusEnum.FINANCIAL_APPROVED:
          nextStatuses = [SplitStatusEnum.CONTRACT_SENT];
          message = "Contrato enviado ao cliente.";
          break;
        case SplitStatusEnum.CONTRACT_SENT:
          // Assinatura encerra o processo: registra a assinatura e aprova o
          // split na mesma ação.
          nextStatuses = [
            SplitStatusEnum.CONTRACT_SIGNED,
            SplitStatusEnum.SPLIT_APPROVED,
          ];
          message = "Contrato assinado e split aprovado com sucesso!";
          break;
        case SplitStatusEnum.CONTRACT_SIGNED:
          nextStatuses = [SplitStatusEnum.SPLIT_APPROVED];
          message = "Split aprovado e registrado com sucesso!";
          break;
        default:
          return;
      }

      const finalStatus = nextStatuses[nextStatuses.length - 1];

      if (selectedCorban?.splitSolicitation?.id) {
        for (const status of nextStatuses) {
          await updateSplitSolicitationStatus({
            corbanId: selectedCorban.id,
            status,
            solicitationId: selectedCorban?.splitSolicitation?.id,
          });
        }
      }

      if (
        SplitStatusEnum.SPLIT_APPROVED === finalStatus &&
        selectedCorban?.FinancialSettings?.id
      ) {
        await updateFinancialSettings(selectedCorban?.FinancialSettings?.id, {
          status: "APPROVED",
        });
        await updateCorban(selectedCorban.id, {
          status: "APPROVED",
          paymentSplit: true,
        });
      }

      toast({
        title: "Aprovado com sucesso",
        description: message,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await fetchPendingCorbans();
      onCloseAnalysis();
    } catch (error) {
      toast({
        title: "Erro ao aprovar",
        description: "Não foi possível aprovar o corban. Tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!selectedCorban?.id) {
      return;
    }

    setRejectModalLoading(true);
    try {
      const currentStatus = getSplitStatus(selectedCorban);
      let rejectedBy: "FINANCIAL" | "CADASTRO" = "FINANCIAL";
      let nextStatus = SplitStatusEnum.FINANCIAL_REJECTED;

      switch (currentStatus) {
        case SplitStatusEnum.CREATED:
        case SplitStatusEnum.FINANCIAL_ANALYSIS:
          rejectedBy = "FINANCIAL";
          nextStatus = SplitStatusEnum.FINANCIAL_REJECTED;
          break;
        case SplitStatusEnum.FINANCIAL_APPROVED:
        case SplitStatusEnum.CONTRACT_SENT:
        case SplitStatusEnum.CONTRACT_SIGNED:
        case SplitStatusEnum.SPLIT_APPROVED:
          rejectedBy = "FINANCIAL"; // Financeiro pode recusar a qualquer momento
          nextStatus = SplitStatusEnum.FINANCIAL_REJECTED;
          break;
        default:
          return;
      }

      // Revoga apenas o split, não o cadastro: o corban segue operando sob a
      // promotora com o split desativado
      await updateCorban(selectedCorban.id, {
        status: "APPROVED",
        financialSettingsId: null,
        paymentSplit: false,
      });

      // Deletar FinancialSettings se existir. Falha aqui não pode reverter a
      // revogação já aplicada acima
      if (selectedCorban?.FinancialSettings?.id) {
        try {
          await deleteFinancialSettings(selectedCorban.FinancialSettings.id);
        } catch (error) {
          console.error("Erro ao deletar FinancialSettings:", error);
        }
      }

      try {
        if (selectedCorban?.splitSolicitation?.id) {
          await updateSplitSolicitationStatus({
            corbanId: selectedCorban.id,
            status: nextStatus,
            rejectionReason: reason,
            rejectedBy,
            solicitationId: selectedCorban?.splitSolicitation?.id,
          });
        }
      } catch (error) {
        console.error(
          "Erro ao atualizar status da solicitação de split:",
          error
        );
      }

      toast({
        title: "Split recusado pelo Financeiro",
        description:
          "O split foi desativado e o motivo foi enviado ao cliente. O corban segue ativo.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setIsRejectModalOpen(false);
      await fetchPendingCorbans();
      onCloseAnalysis();
    } catch (error: any) {
      console.error("Erro ao recusar split:", error);
      toast({
        title: "Erro ao recusar",
        description:
          error?.response?.data?.message ||
          "Não foi possível recusar o split. Tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRejectModalLoading(false);
    }
  };

  const openRejectModal = () => {
    setIsRejectModalOpen(true);
  };

  const FilteredCorbanData = corbans || [];

  // ── render ──

  return (
    <>
      {FilteredCorbanData.length !== 0 ? (
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          px={0}
          py={{ base: 2, md: 4 }}
          mt={4}
        >
          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={FilteredCorbanData.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />

          <TableContainer borderRadius="lg" overflowX="auto">
            <Table variant="simple">
              <Thead {...backgroundStyle}>
                <Tr>
                  <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX} w="220px">
                    AÇÕES
                  </Th>
                  {[
                    { label: "ID", field: "id" },
                    { label: "CORBAN", field: "businessName" },
                    { label: "CNPJ", field: "document" },
                    { label: "RAZÃO SOCIAL", field: "legalName" },
                    {
                      label: "RESPONSÁVEL PELA OPERAÇÃO",
                      field: "commercialManager",
                    },
                    {
                      label: "STATUS DO SPLIT DE PAGAMENTO",
                    }
                  ].map(({ label, field }, idx, arr) => {
                    const isSortable = Boolean(field);
                    const sortIcon =
                      sortBy === field
                        ? sortDirection === "asc"
                          ? FaSortUp
                          : FaSortDown
                        : FaSort;
                    return (
                      <Th
                        key={field ?? label}
                        {...tableHeaderStyles}
                        px={idx === arr.length - 1 ? EDGE_COLUMN_PX : 1}
                        cursor={isSortable ? "pointer" : "default"}
                        userSelect="none"
                        onClick={() => field && handleSort(field)}
                        _hover={isSortable ? { opacity: 0.85 } : undefined}
                      >
                        <Flex align="center" justify="center" gap={1.5}>
                          {label}
                          {isSortable && <Icon as={sortIcon} boxSize={3} />}
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {FilteredCorbanData.map((corban) => (
                  <Tr key={corban.id}>
                    <Td
                      {...tableCellStyles}
                      px={EDGE_COLUMN_PX}
                      py={{ base: 2, md: 3 }}
                    >
                      <Flex
                        justify="center"
                        flexDirection="column"
                        gap={2}
                        alignItems="center"
                      >
                        {(
                          <Button
                            size="md"
                            colorScheme={"green.500"}
                            // {...tagButtonStyle(getSplitStatusColor(corban))}
                            width="90%"
                            onClick={() => (
                              setSelectedCorban(corban), onOpenAnalysis()
                            )}
                            py={2}
                            fontSize="sm"
                            fontWeight="bold"
                            fontFamily="Lexend Deca"
                          >
                            {"Analisar"}
                          </Button>
                        )}
                      </Flex>
                    </Td>
                    <Td {...tableCellStyles}>
                      <Link
                        as={RouterLink}
                        to={`/corban/editar/${corban.id}`}
                        color="blue.500"
                      >
                        {corban.id}
                      </Link>
                    </Td>
                    <Td {...tableCellStyles}>{corban.businessName}</Td>
                    <Td {...tableCellStyles} whiteSpace="nowrap">
                      {documentFormatter(corban.document)}
                    </Td>
                    <Td {...tableCellStyles}>{corban.legalName}</Td>
                    <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                      {corban.promoterMasterUser
                        ? `${corban.promoterMasterUser.masterUserName ?? ""} ${
                            corban.promoterMasterUser.masterUserLastName ?? ""
                          }`.trim()
                        : "—"}
                    </Td>
                    <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                      {(() => {
                        const status = corban.splitSolicitation
                          ?.status as SplitStatusEnum | undefined;
                        const label =
                          status && SplitStatusLabels[status]
                            ? SplitStatusLabels[status]
                            : "Não Solicitado";
                        const color =
                          status && SplitStatusColors[status]
                            ? SplitStatusColors[status]
                            : "gray";
                        return (
                          <Badge
                            colorScheme={color}
                            px={2}
                            py={1}
                            borderRadius="md"
                            whiteSpace="normal"
                            textAlign="center"
                          >
                            {label}
                          </Badge>
                        );
                      })()}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={FilteredCorbanData.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </Box>
      ) : (
        <Heading mt={100} textAlign={"center"} color="gray.600">
          {statusFilter
            ? "Nenhuma solicitação encontrada para este status"
            : "Nenhuma solicitação de split encontrada"}
        </Heading>
      )}

      {/* Modal de análise de dados para split */}
      <Modal
        isOpen={isAnalysisOpen}
        onClose={onCloseAnalysis}
        size="6xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          maxW="1200px"
          maxH="90vh"
          borderRadius="2xl"
          overflow="hidden"
        >
          <ModalHeader
            px={6}
            py={5}
            borderBottomWidth="1px"
            borderColor="gray.100"
          >
            <HStack spacing={3} align="center" pr={8}>
              <Flex
                align="center"
                justify="center"
                boxSize={10}
                borderRadius="lg"
                bg="blue.50"
                color="blue.500"
                flexShrink={0}
              >
                <Icon as={FaFileInvoiceDollar} boxSize={5} />
              </Flex>
              <Box>
                <Heading size="md" fontFamily="Lexend Deca" color="gray.800">
                  Análise de Dados para Split
                </Heading>
                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                  {selectedCorban?.businessName}
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={5} right={5} borderRadius="lg" />
          <ModalBody bg="gray.50" px={6} py={6}>
            <AnalizeSplitApproveData
              financialSettings={selectedCorban?.FinancialSettings}
              corban={selectedCorban}
              masterUser={selectedCorban?.masterUser}
              splitStatus={
                selectedCorban ? getSplitStatus(selectedCorban) : null
              }
            />
          </ModalBody>
          <ModalFooter
            px={6}
            py={4}
            borderTopWidth="1px"
            borderColor="gray.100"
            bg="white"
          >
            <Flex
              width="100%"
              justify="space-between"
              align={{ base: "stretch", md: "center" }}
              direction={{ base: "column", md: "row" }}
              gap={3}
            >
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.500">
                  Status atual
                </Text>
                <Tag
                  size="md"
                  borderRadius="full"
                  colorScheme={
                    selectedCorban
                      ? getSplitStatusColor(selectedCorban)
                      : "gray"
                  }
                  px={3}
                  py={1.5}
                >
                  {selectedCorban
                    ? getSplitStatusLabel(selectedCorban)
                    : "Sem status"}
                </Tag>
              </HStack>
              <HStack spacing={3} justify={{ base: "stretch", md: "flex-end" }}>
                {(() => {
                  const currentStatus = selectedCorban
                    ? getSplitStatus(selectedCorban)
                    : null;
                  const isFinalState =
                    currentStatus === SplitStatusEnum.FINANCIAL_REJECTED ||
                    currentStatus === SplitStatusEnum.CADASTRO_REJECTED;

                  return (
                    <>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        onClick={openRejectModal}
                        minW="140px"
                        isDisabled={
                          isApproving || !selectedCorban || isFinalState
                        }
                      >
                        {getRejectButtonText(currentStatus)}
                      </Button>
                      {currentStatus !== SplitStatusEnum.SPLIT_APPROVED && (
                        <Button
                          colorScheme="green"
                          onClick={handleApprove}
                          minW="140px"
                          isLoading={isApproving}
                          loadingText="Processando..."
                          isDisabled={
                            isApproving ||
                            !selectedCorban ||
                            isFinalState ||
                            !currentStatus
                          }
                        >
                          {getApproveButtonText(currentStatus)}
                        </Button>
                      )}
                    </>
                  );
                })()}
              </HStack>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <RejectReasonModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={handleReject}
        isLoading={rejectModalLoading}
      />
    </>
  );
}
